import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, Paper, Typography, IconButton } from "@mui/material";
import { Delete, History, Info, CheckCircle, Error } from "@mui/icons-material";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import TablePaginationComponent from "../table/table-pagination.component";
import DialogVersion from "../dialog/dialog.component";
import { ImportHistory, ImportType } from "../../models/general/import";
import Loading from "../loading/loading";
import HistoryComponent from "../history/history.component";
import { PaginatedResult } from "../../models/default/paginated-result";
import { BaseService } from "../../services/base-service";
import { URLS } from "../../services/app-urls";
import { ErrorComponent } from "../error/error.component";
import { translate } from "../../translate/translate";
import { Analysis } from "../../models/general/analysis";
import ForecastTypeBadge from "../forecast/forecast-type-badge.component";


const ImportHistoryComponent = ({ import_type, displayedColumnsShow, displayedColumns, onlyInfoAction }: { import_type: ImportType; displayedColumnsShow?: string[]; displayedColumns?: string[]; onlyInfoAction?: boolean }) => {
  const service = new BaseService<Analysis>(URLS.IMPORT_HISTORY);
  const [importHistory, setImportHistory] = useState<ImportHistory>();
  const [dataSource, setDataSource] = useState<ImportHistory[] | any>([]);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openHistory, setOpenHistory] = useState<boolean>(false);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [idHistory, setIdHistory] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const search = async (restartIndex = false) => {
    try {
      setIsLoading(true);
      const service = new BaseService<ImportHistory>(URLS.IMPORT_HISTORY);
      service.clearParameters();
      service.addParameter("limit", rowsPerPage);
      service.addParameter("offset", page * rowsPerPage);
      service.addParameter("import_type", import_type);

      const response = await service.getAllPaginate();

      setPagination(response.data);

      const formattedData = response.data.results.map((item: ImportHistory) => ({
        ...item,
      }));

      setDataSource(formattedData);

      if (restartIndex) setPage(0);
    } catch (error) {
      ErrorComponent(error as AxiosError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    search();
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderStatusCell = (message: string, isSuccess: boolean) => (
    <Box display="flex" alignItems="center" textAlign={"left"} maxWidth={{ xs: "200px", lg: "300px", xl: "unset" }}>
      {isSuccess ? (
        <CheckCircle sx={{ color: "#4CAF4F", marginRight: "8px" }} />
      ) : (
        <Error sx={{ color: "#F44336", marginRight: "8px" }} />
      )}
      {message}
    </Box>
  );

  const handleOpenDeleteModal = (row: ImportHistory) => {
    setImportHistory(row);
    setOpenDeleteModal(true);
  }

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.length === dataSource.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(dataSource.map((item: ImportHistory) => item.id));
    }
  }, [selectedIds, dataSource]);

  const toggleSelectOne = useCallback((id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, []);

  const renderCheckboxCell = useCallback(
    (row: ImportHistory) => {

      return (
        <Box display="flex" alignItems="left" justifyContent="left" sx={{ height: "100%" }}>
          <Checkbox
            checked={selectedIds.includes(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelectOne(row.id)}
            color="primary"
            sx={{
              "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
          />
        </Box>
      );
    },
    [selectedIds, toggleSelectOne]
  );

  const renderHeaderCheckbox = useCallback(
    () => {
      return (
        <Box display="flex" alignItems="left" justifyContent="left" sx={{ height: "100%" }}>
          <Checkbox
            indeterminate={
              selectedIds.length > 0 && selectedIds.length < dataSource.length
            }
            checked={
              selectedIds.length === dataSource.length && dataSource.length > 0
            }
            onChange={toggleSelectAll}
            sx={{
              "& .MuiSvgIcon-root": { fontSize: 20 },
              color: "white",
              "& .MuiSvgIcon-root path": {
                stroke: "white",
              },
              "&.Mui-checked": {
                color: "white",
              },
              "&.MuiCheckbox-indeterminate": {
                color: "white",
              },
              "& .MuiTouchRipple-root": {
                color: "white",
              },
            }}
          />
        </Box>
      );
    },
    [selectedIds, dataSource, toggleSelectAll]
  );

  const renderCellHandlerConfig = useMemo(() => ({
    select: (_: string, __: number, row: any) => renderCheckboxCell(row),
    status_message: (_: string, __: number, row: any) => renderStatusCell(row["status_message"], row["is_success"]),
    type: (_: string, __: number, row: ImportHistory) => <ForecastTypeBadge type={row.type} />,
    // Se onlyInfoAction estiver ativado, renderiza somente o ícone de informação
    action: (_: string, __: number, row: any) => {
      if (!onlyInfoAction) return null;
      return (
        <IconButton
          sx={{ color: "#546E7A" }}
          size="small"
          onClick={(e) => { e.stopPropagation(); setIdHistory(row.id); setOpenHistory(true); }}
        >
          <Info />
        </IconButton>
      );
    },
  }), [renderCheckboxCell, onlyInfoAction]);

  const confirmDeleteMessage = useMemo(() => {
    if (selectedIds.length > 1) {
      return translate.t("confirm_delete_itens");
    }
    return translate.t("confirm_delete");
  }, [selectedIds]);

  const handleCloseDeleteModal = useCallback(
    async (result: unknown) => {
      if (typeof result === "boolean" && result) {
        const idsToDelete =
          selectedIds.length > 0 ? selectedIds : importHistory ? [importHistory.id] : [];

        if (idsToDelete.length === 0) {
          setOpenDeleteModal(false);
          return;
        }

        try {
          setIsLoading(true);

          await service.postFromListRoute({ "ids": idsToDelete, }, "bulk-delete");

          toast.success(
            idsToDelete.length > 1
              ? translate.t("all_successfully_deleted")
              : translate.t("successfully_deleted")
          );

          search();
        } catch (error: any) {
          ErrorComponent(error as any);
        } finally {
          setIsLoading(false);
          setSelectedIds([]);
          setImportHistory(undefined);
          setOpenDeleteModal(false);
        }
      } else {
        setImportHistory(undefined);
        setOpenDeleteModal(false);
      }
    },
    [selectedIds, importHistory, service, search]
  );

  const renderSelectedImports = useCallback(() => {
    const imports = dataSource.filter((i: any) => selectedIds.includes(i.id));
    if (imports.length === 0) return null;

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: "10px",
          border: "1px solid #E0E0E0",
          backgroundColor: "#FAFAFA",
          padding: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: 300,
          overflowY: "overlay",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {imports.map((item: any, idx: number) => {
          return (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: "40px",
              }}
            >
              {/* Descrição */}
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  color: "#212121",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingLeft: "12px",
                }}
              >
                {item?.file_name}
              </Typography>
            </Paper>
          );
        })}
      </Paper>
    );
  }, [dataSource, selectedIds]);

  return (
    <>
      <Loading open={isLoading} />
      <Box p={3}>
        <Box sx={{ border: "1px solid #CFD8DC" }} />
      </Box>
      <Box p={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <History sx={{ color: "#546E7A" }} />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "24.2px",
              textAlign: "left",
            }}
          >
            {translate.t("import_history")}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center">
          {selectedIds.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#FFF8E1",
                border: "1px solid #F8B653",
                borderRadius: "8px",
                padding: "12px 16px",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Info sx={{ color: "#FBC02D" }} />
                <Typography sx={{ color: "#6D4C41", fontWeight: 500 }}>
                  {selectedIds.length}{" "}
                  {selectedIds.length === 1
                    ? translate.t("selected_record")
                    : translate.t("selected_records")}
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="secondary"
                startIcon={<Delete />}
                sx={{
                  backgroundColor: "#3949AB",
                  "&:hover": { backgroundColor: "#1F0063" },
                  textTransform: "none",
                  fontWeight: 600,
                }}
                onClick={() => {
                  selectedIds.forEach((id) => {
                    const selected = dataSource.find((item: ImportHistory | any) => item.id === id);
                    if (selected) handleOpenDeleteModal(selected);
                  });
                }}
              >
                {[translate.t("delete_item")]}
              </Button>
            </Box>
          )}
        </Box>

        <Box mt={3}>
          <TablePaginationComponent
            state={dataSource}
            displayedColumnsShow={displayedColumnsShow ?? (import_type === ImportType.FORECAST
              ? ["select", "import_date", "document", "user", "type", "customer", "status"]
              : ["select", "import_date", "document", "user", "status"])}
            displayedColumns={displayedColumns ?? (import_type === ImportType.FORECAST
              ? ["select", "import_date", "file_name", "user_name", "type", "customer_name", "status_message"]
              : ["select", "import_date", "file_name", "user_name", "status_message"])}
            dateColumns={["import_date"]}
            count={pagination?.count}
            next={pagination?.next}
            previous={pagination?.previous}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            renderCellHandler={renderCellHandlerConfig}
            renderHeaderCheckbox={renderHeaderCheckbox}
            handleOpenDialogHistory={(id: number) => { setIdHistory(id); setOpenHistory(true); }}
          />
        </Box>

        {openDeleteModal && (
          <DialogVersion
            id="dialog-delete-import"
            open={openDeleteModal}
            onClose={handleCloseDeleteModal}
            title={translate.t("delete_import_history")}
            message={confirmDeleteMessage}
            labelButton={translate.t("delete")}
            colorButton="error"
          >
            {renderSelectedImports()}
          </DialogVersion>
        )}
        {/* Histórico detalhado (dialog) */}
        {openHistory && (
          <HistoryComponent
            openHistory={openHistory}
            handleCloseDialogHistory={() => setOpenHistory(false)}
            idHistory={idHistory}
            columns_history={["file_name", "status_message", "user_name", "import_date"]}
            service={URLS.IMPORT_HISTORY}
            dateColumns={["import_date"]}
          />
        )}
      </Box>
    </>
  );
};

export default ImportHistoryComponent;
